/**
 * RecipeBook Component
 * 
 * Purpose: Public-facing recipe browsing page with search, filter, and grid layout
 * Features: Search by name/ingredients, filter by tags, sort by newest/rating/cook time
 */

import React, { useState, useEffect } from 'react';
import RecipeCard from './RecipeCard';
import config from '../config/environments';

const API_URL = config.api.baseUrl;

const RecipeBook = ({ onRecipeClick, onBack }) => {
  const [recipes, setRecipes] = useState([]);
  const [filteredRecipes, setFilteredRecipes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filter and search states
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [sortBy, setSortBy] = useState('newest');

  // Available tags for filtering
  const availableTags = [
    'Breakfast',
    'Lunch',
    'Dinner',
    'Dessert',
    'Shabbat',
    'Quick Meals',
    'Vegetarian',
    'Meat',
    'Dairy',
    'Pareve',
    'Appetizer',
    'Side Dish',
    'Soup',
    'Salad',
    'Pasta',
    'Baking'
  ];

  // Fetch recipes from API
  useEffect(() => {
    fetchRecipes();
  }, []);

  // Apply filters and search whenever they change
  useEffect(() => {
    applyFilters();
  }, [recipes, searchTerm, selectedTags, sortBy]);

  const fetchRecipes = async () => {
    try {
      setIsLoading(true);
      const response = await fetch(`${API_URL}/api/recipes`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch recipes');
      }
      
      const data = await response.json();
      
      if (data.success) {
        setRecipes(data.recipes);
      } else {
        setError(data.message || 'Failed to load recipes');
      }
    } catch (err) {
      console.error('Error fetching recipes:', err);
      setError('Could not load recipes. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...recipes];

    // Apply search filter
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(recipe => 
        recipe.name.toLowerCase().includes(search) ||
        (recipe.description && recipe.description.toLowerCase().includes(search)) ||
        (recipe.ingredients && JSON.stringify(recipe.ingredients).toLowerCase().includes(search))
      );
    }

    // Apply tag filter
    if (selectedTags.length > 0) {
      filtered = filtered.filter(recipe =>
        recipe.tags && selectedTags.some(tag => recipe.tags.includes(tag))
      );
    }

    // Apply sorting
    switch (sortBy) {
      case 'rating':
        filtered.sort((a, b) => (b.admin_rating || 0) - (a.admin_rating || 0));
        break;
      case 'cook_time':
        filtered.sort((a, b) => (a.cook_time_minutes || 999) - (b.cook_time_minutes || 999));
        break;
      case 'newest':
      default:
        filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        break;
    }

    setFilteredRecipes(filtered);
  };

  const toggleTag = (tag) => {
    if (selectedTags.includes(tag)) {
      setSelectedTags(selectedTags.filter(t => t !== tag));
    } else {
      setSelectedTags([...selectedTags, tag]);
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedTags([]);
    setSortBy('newest');
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="text-6xl mb-4 animate-bounce">👨‍🍳</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Loading Recipes...</h2>
          <p className="text-gray-600">Gathering the best recipes for you!</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50 flex items-center justify-center p-4">
        <div className="max-w-md mx-auto text-center bg-white rounded-xl p-8 shadow-lg">
          <div className="text-6xl mb-4">😕</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Oops!</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={fetchRecipes}
            className="px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-lg font-semibold hover:from-green-600 hover:to-emerald-600 transition-all"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-500 via-red-500 to-pink-500 text-white shadow-xl">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
              {onBack && (
                <button
                  onClick={onBack}
                  className="mr-4 p-2 hover:bg-white/20 rounded-full transition-all"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
              )}
              <div>
                <h1 className="text-4xl md:text-5xl font-black mb-2">
                  🍳 Recipe Book
                </h1>
                <p className="text-lg opacity-90">
                  Discover delicious recipes curated by our community
                </p>
              </div>
            </div>
            <div className="hidden md:block text-right">
              <div className="text-3xl font-bold">{filteredRecipes.length}</div>
              <div className="text-sm opacity-90">Recipes</div>
            </div>
          </div>

          {/* Search Bar */}
          <div className="max-w-2xl">
            <div className="relative">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search recipes by name, ingredients, or description..."
                className="w-full px-6 py-4 pr-12 rounded-full text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-4 focus:ring-white/50 shadow-lg"
              />
              <svg
                className="absolute right-4 top-1/2 transform -translate-y-1/2 w-6 h-6 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Sort */}
      <div className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          {/* Sort Options */}
          <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-gray-700">Sort by:</span>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-orange-500 focus:outline-none text-sm"
              >
                <option value="newest">Newest First</option>
                <option value="rating">Highest Rated</option>
                <option value="cook_time">Quickest to Make</option>
              </select>
            </div>

            {(searchTerm || selectedTags.length > 0) && (
              <button
                onClick={clearFilters}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium transition-all"
              >
                Clear Filters
              </button>
            )}
          </div>

          {/* Tag Filter Pills */}
          <div className="flex flex-wrap gap-2">
            {availableTags.map(tag => (
              <button
                key={tag}
                onClick={() => toggleTag(tag)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                  selectedTags.includes(tag)
                    ? 'bg-gradient-to-r from-orange-500 to-pink-500 text-white shadow-md'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {tag}
              </button>
            ))}
          </div>

          {/* Active Filters Display */}
          {selectedTags.length > 0 && (
            <div className="mt-3 pt-3 border-t border-gray-200">
              <span className="text-sm text-gray-600">
                Filtering by: {selectedTags.join(', ')}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Recipe Grid */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {filteredRecipes.length === 0 ? (
          <div className="text-center py-16">
            <div className="text-6xl mb-4">🔍</div>
            <h3 className="text-2xl font-bold text-gray-800 mb-2">No recipes found</h3>
            <p className="text-gray-600 mb-6">
              {searchTerm || selectedTags.length > 0
                ? 'Try adjusting your filters or search terms'
                : 'No recipes have been added yet. Check back soon!'}
            </p>
            {(searchTerm || selectedTags.length > 0) && (
              <button
                onClick={clearFilters}
                className="px-6 py-3 bg-gradient-to-r from-orange-500 to-pink-500 text-white rounded-lg font-semibold hover:from-orange-600 hover:to-pink-600 transition-all"
              >
                Clear All Filters
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="mb-6 text-gray-600">
              Showing {filteredRecipes.length} {filteredRecipes.length === 1 ? 'recipe' : 'recipes'}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredRecipes.map(recipe => (
                <RecipeCard
                  key={recipe.id}
                  recipe={recipe}
                  onClick={onRecipeClick}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default RecipeBook;

